/**
 * App.jsx
 * Root application layout (NO AuthProvider here)
 */

import React, { Suspense, lazy, useEffect } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation
} from "react-router-dom";

// Styles
import "./styles/theme.css";
import "./App.css";
import "./styles/skip-link.css";

// Layout
import Navbar from "./components/Navbar";
import Footer from "./components/Footer.jsx";
import ThemeToggle from "./components/ThemeToggle";

// Auth
import { useAuth } from "./context/AuthContext.jsx";

// Eager pages
import UnifiedLanding from "./pages/UnifiedLanding";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AdminSetupForm from "./components/AdminSetupForm";

/* ---------------- Lazy Loader with Retry ---------------- */
function lazyWithRetry(factory, { retries = 1, delay = 800 } = {}) {
  let attempts = 0;
  return lazy(() =>
    factory().catch((err) => {
      const msg = String(err?.message || err);
      const isChunk =
        /ChunkLoadError|Loading chunk|Failed to fetch|dynamically imported/.test(msg);

      if (isChunk && attempts < retries) {
        attempts++;
        return new Promise((resolve) =>
          setTimeout(() => resolve(factory()), delay)
        );
      }
      throw err;
    })
  );
}

/* ---------------- Lazy Pages ---------------- */
const DashboardEntry = lazyWithRetry(() => import("./pages/DashboardEntry"));
const DashboardPage  = lazyWithRetry(() => import("./pages/DashboardPage"));
const FAQPage        = lazyWithRetry(() => import("./pages/FAQPage"));
const AboutPage      = lazyWithRetry(() => import("./pages/AboutPage"));
const ContactPage    = lazyWithRetry(() => import("./pages/ContactPage"));
const AdminSetup     = lazyWithRetry(() => import("./pages/AdminSetup"));
const PromoPage      = lazyWithRetry(() => import("./pages/PromoPage"));

/* ---------------- Scroll Restoration ---------------- */
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

/* ---------------- Protected Route ---------------- */
function Protected({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Checking auth…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ✅ FIX: map roles to isAdmin
  if (roles?.includes("admin") && !user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/* ---------------- App Root ---------------- */
export default function App() {
  return (
    <Router>
      <Navbar />
      <ThemeToggle />

      <div id="main-content" className="main-content" tabIndex="-1">
        <ScrollToTop />

        <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>}>
          <Routes>
            <Route path="/" element={<UnifiedLanding />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />

            <Route
              path="/dashboard-entry"
              element={<Protected><DashboardEntry /></Protected>}
            />
            <Route
              path="/dashboard"
              element={<Protected><DashboardPage /></Protected>}
            />

            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />

            <Route
              path="/admin-setup"
              element={<Protected roles={["admin"]}><AdminSetup /></Protected>}
            />
            <Route
              path="/admin-setup-form"
              element={<Protected roles={["admin"]}><AdminSetupForm /></Protected>}
            />

            <Route path="/promo/:code" element={<PromoPage />} />

            <Route
              path="*"
              element={
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <h2>Page Not Found</h2>
                  <Link to="/" className="cta-main-btn">Go Home</Link>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </div>

      <Footer />
    </Router>
  );
}
